import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Link, Stack, Typography } from "@mui/material";

import {
  OnboardingStepLayout,
  TextInput,
  CheckboxField,
} from "./../../../shared/ui/index";
import { Step5Schema, type Step5Input } from "../../../shared/schemas/step5";
import { useTranslation, Trans } from "react-i18next";
import { SpecialTariffs } from "../../../components/SpecialTariffs";

export type Step5ContactFormValues = Step5Input;

type Step5ContactProps = {
  currentStep: number;
  totalSteps: number;
  initialValues?: Step5ContactFormValues;
  onCompleted: (data: Step5ContactFormValues) => void;
  onBack: () => void;
};

const CONTRACT_PDF_URL =
  "https://atlante.energy/wp-content/uploads/2026/05/CONTRATO-DE-ADESAO-AO-SERVICO-MYATLANTE-FLEET_13_05_2026.pdf";
const PRIVACY_NOTICE_PT_PDF_URL =
  "https://atlante.energy/wp-content/uploads/2025/07/Privacy-note_myAtlante_app_-PT_july25.pdf";
const PRIVACY_NOTICE_EN_PDF_URL =
  "https://atlante.energy/wp-content/uploads/2025/07/Privacy-note_myAtlante_app_-ENG_july25.pdf";

export default function Step5_ContactPerson({
  currentStep,
  totalSteps,
  initialValues,
  onCompleted,
  onBack,
}: Step5ContactProps) {
  const methods = useForm<Step5ContactFormValues>({
    resolver: zodResolver(Step5Schema),
    defaultValues: {
      contactFullName: "",
      acceptContractEmail: false,
      acceptTerms: false,
      acceptPrivacyNotice: false,
      ...initialValues,
    },
  });

  const { handleSubmit } = methods;
  const { t, i18n } = useTranslation();
  const privacyNoticeUrl = i18n.resolvedLanguage?.startsWith("pt")
    ? PRIVACY_NOTICE_PT_PDF_URL
    : PRIVACY_NOTICE_EN_PDF_URL;

  const onSubmit = handleSubmit((data) => {
    sessionStorage.setItem("step5", JSON.stringify(data));
    onCompleted(data);
  });

  return (
    <FormProvider {...methods}>
      <OnboardingStepLayout
        step={currentStep}
        totalSteps={totalSteps}
        title={t("onboarding.step5.title")}
        subtitle=""
        onNext={onSubmit}
        nextLabel={t("onboarding.step5.actions.next")}
        onBack={onBack}
        backLabel={t("onboarding.step5.actions.back")}
      >
        <Box sx={{ maxWidth: 520 }}>
          <Stack spacing={4}>
            <Box>
              <SpecialTariffs />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#1A1A1A",
                  mb: 0.5,
                }}
              >
                {t("onboarding.step5.contactPerson.title")}
              </Typography>

              <Stack spacing={2.5} mt={1.5}>
                <TextInput<Step5ContactFormValues, "contactFullName">
                  name="contactFullName"
                  label={t("onboarding.step5.contactPerson.fullName.label")}
                  placeholder={t(
                    "onboarding.step5.contactPerson.fullName.placeholder"
                  )}
                  required
                />

                <div style={{ marginLeft: "12px" }}>
                  
                  <CheckboxField<Step5ContactFormValues, "acceptTerms">
                    name="acceptTerms"
                    label={
                      <Box component="span">
                        <Trans
                          i18nKey="onboarding.step5.contactPerson.acceptTerms.label"
                          components={[
                            <Link
                              key="terms"
                              href={CONTRACT_PDF_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontWeight: 600 }}
                            />,
                          ]}
                        />
                      </Box>
                    }
                  />

                  <CheckboxField<Step5ContactFormValues, "acceptContractEmail">
                    name="acceptContractEmail"
                    label={t(
                      "onboarding.step5.contactPerson.acceptContractEmail.label"
                    )}
                  />

                  <CheckboxField<Step5ContactFormValues, "acceptPrivacyNotice">
                    name="acceptPrivacyNotice"
                    label={
                      <Box component="span">
                        <Trans
                          i18nKey="onboarding.step5.contactPerson.acceptPrivacyNotice.label"
                          components={[
                            <Link
                              key="privacy"
                              href={privacyNoticeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontWeight: 600 }}
                            />,
                          ]}
                        />
                      </Box>
                    }
                  />
                </div>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </OnboardingStepLayout>
    </FormProvider>
  );
}
